import { isEmpty } from 'lodash-es';

import { DNode } from '@dojo/framework/core/interfaces';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { BreadcrumbGroup, BreadcrumbItem } from '@dojo/widgets/breadcrumb-group';
import { Keys } from '@holmescorp/common';
import { createNlsMiddleware, keymap, lccstate, pathmap } from '@holmescorp/lcc-state';
import { TopicDetails } from '@holmescorp/lcc-state/interfaces';
import { publish } from '@holmescorp/pub-sub';
import { Icon } from '@holmescorp/base-widgets';

import css from '../css/index.m.css';

const factory = create({
	settings: createNlsMiddleware('settings'),
	theme,
	lccstate
});

const nlsKeyMap: Record<string, string> = {
	quiz: 'quiz_title',
	'my-progress': 'progress_title',
	glossary: 'glossary_title',
	'final-test': 'final_test_title',
	posttest: 'final_test_title',
	practice: 'final_test_title'
};

export const Breadcrumbs = factory(({ id, children, middleware: { settings, theme, lccstate } }) => {
	const loc = new URL(window.location.href);
	const site = window.hcstatic.portal_site;
	const baseUrl = window.hcstatic.portal_url;

	const messages = settings().messages;

	const crumb = (key: string, last: boolean = false): DNode => {
		// short circuit if the key is nlsMapped
		if (key in nlsKeyMap) {
			return v('span', { classes: [css.last] }, [messages[nlsKeyMap[key]]]);
		}

		const { path } = keymap.get(key)!;
		const pmap = pathmap.get(path) || { topic: {}, view: () => {} };
		const { topic } = pmap;

		if (topic && !isEmpty(topic)) {
			const values = Object.values(topic);
			const { attributes } = Array.isArray(values) && values.length ? values[0] : ({} as TopicDetails);
			const title = attributes && 'title' in attributes ? attributes.title : key;
			const { view } = pmap;
			return last
				? v('span', { classes: [css.last] }, [title])
				: v(
						'a',
						{
							href: '',
							onclick: (event: MouseEvent) => {
								event.preventDefault();
								view();
							},
							onkeydown: (event: KeyboardEvent) => {
								switch (event.key) {
									case Keys.Enter:
									case Keys.Space:
									case Keys.Spacebar:
										event.preventDefault();
										view();
								}
							},
							title
						},
						[title]
				  );
		} else {
			const href = `${baseUrl}/${key}`;
			const title = key in nlsKeyMap ? messages[nlsKeyMap[key]] : key;
			return last ? v('span', {}, [title]) : v('a', { href, title }, [title]);
		}
	};

	if (loc) {
		const paths = loc.pathname.split('/').filter((p) => p !== '' && p !== site && p !== 'explore');
		const { get, path } = lccstate;
		const roots: string[] = get(path('ditaroots'));
		const pathList = paths.filter((p): boolean => !roots.includes(p));

		const items = pathList.map((p, i): DNode => {
			return crumb(p, i + 1 === pathList.length);
		});

		items.unshift(
			v(
				'a',
				{
					href: '',
					classes: [css.home],
					onclick: (event: MouseEvent) => {
						event.preventDefault();
						publish('site/homepage');
					},
					onkeydown: (event: KeyboardEvent) => {
						event.preventDefault();
						publish('site/homepage');
					},
					title: 'Home'
				},
				[w(Icon, { faname: 'house' })]
			)
		);

		const sep = v('span', { classes: [] }, [' / ']);
		const crumbs = items
			? items
					.map((node, index, array) => {
						const resultArray: DNode[] = [node];
						if (index < array.length - 1) {
							resultArray.push(sep);
						}
						return resultArray;
					})
					.flat()
			: [null];

		return v('div', { id, xqa: 'breadcrumb-group', classes: [theme.variant(), css.breadCrumbs] }, [
			...crumbs,
			...children()
		]);
	}
});

export const DojoBreadcrumbs = factory(({ id, middleware: { settings, theme, lccstate } }) => {
	const home = 'Home';
	const loc = new URL(window.location.href);
	const site = window.hcstatic.portal_site;
	const baseUrl = window.hcstatic.portal_url;

	const messages = settings().messages;

	if (loc) {
		const paths = loc.pathname.split('/').filter((p) => p !== '' && p !== site && p !== 'explore');
		const { get, path } = lccstate;
		const roots: string[] = get(path('ditaroots'));

		const pathList = paths.filter((p): boolean => !roots.includes(p));

		const items = pathList.map((p, i): BreadcrumbItem => {
			const { path } = keymap.get(p)!;
			const { topic } = pathmap.get(path)!;
			const href = i + 1 < pathList.length && topic ? `${baseUrl}/${path}` : '';
			const values = Object.values(topic);
			const { attributes } = values.length ? values[0] : ({} as TopicDetails);
			const title = (
				topic && attributes && 'title' in attributes
					? attributes.title
					: p && p in nlsKeyMap
					? messages[nlsKeyMap[p]]
					: p
			) as string;
			const label = title;
			return { label, href, title };
		});
		items.unshift({ label: home, href: `${baseUrl}/explore`, title: home });
		return v('div', { id, xqa: 'breadcrumb-group', classes: [theme.variant(), css.breadCrumbs] }, [
			w(BreadcrumbGroup, { label: 'breadcrumbs', items })
		]);
	}
});

export default Breadcrumbs;
